import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Fader from 'react-fader';
import { Route, useHistory } from 'react-router-dom';
import Switch from 'react-router-transition-switch';

import * as Sentry from '@sentry/react';
import { AxiosResponse } from 'axios';
import queryString from 'query-string';

import RegistrationOnboardingLayout from 'components/RegistrationOnboardingLayout';
import { CountryCode } from 'components/Selects/CountryCodeSelect';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { RestCountryResponse } from 'models/restCountry';
import * as otpService from 'services/otpService';
import { LinkRappiUserRequest } from 'services/registrationService';
import { extractErrorMessage } from 'utils/generic';

import JoinRappi from './forms/JoinRappi';
import RappiAccount from './forms/RappiAccount';
import Telephone, { OTPChannel, TelephoneValues } from './forms/Telephone';
import TwoFactor, { TwoFactorCodeValues, TwoFactorData } from './forms/TwoFactor';
import VerificationCode, { VerificationCodeValues } from './forms/VerificationCode';
import Welcome from './forms/Welcome';

interface Props {
  fetchAvailableCountries: () => Promise<void>;
  fetchCountryCodes: () => Promise<void>;
  linkRappiUser: (data: LinkRappiUserRequest) => Promise<any>;
  restCountries: RestCountryResponse[];
  resendTwoFactor: (key: string) => Promise<AxiosResponse<void>>;
}

const Invite = ({
  fetchAvailableCountries,
  fetchCountryCodes,
  linkRappiUser,
  restCountries,
  resendTwoFactor,
}: Props) => {
  const { t } = useStoreTranslation();
  const toast = useToast();
  const history = useHistory();
  const isMobile = useIsMobile();
  const queryParams = queryString.parse(history.location.search) as { token?: string; email?: string };

  const [tokenParam, setTokenParam] = useState<string | null>(null);
  const [emailParam, setEmailParam] = useState<string | null>(null);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [telephone, setTelephone] = useState<TelephoneValues | null>(null);
  const [otpChannel, setOtpChannel] = useState<OTPChannel>();
  const [verificationCode, setVerificationCode] = useState<VerificationCodeValues | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState<TwoFactorCodeValues | null>(null);
  const [twoFactorData, setTwoFactorData] = useState<TwoFactorData | null>(null);
  const [otpSendResponse, setOtpSendResponse] = useState<
    otpService.CreateWhatsAppOTPResponse | otpService.CreateSMSOTPResponse | null
  >(null);
  const [hasRappiAccount, setHasRappiAccount] = useState<boolean>(false);

  const defaultErrorHandler = useCallback(
    (error: any) => toast.add({ type: 'danger', message: extractErrorMessage(error) || t('defaultError.default') }),
    [t, toast],
  );

  useEffect(() => {
    fetchAvailableCountries().catch(defaultErrorHandler);
    fetchCountryCodes().catch(defaultErrorHandler);
  }, [defaultErrorHandler, fetchAvailableCountries, fetchCountryCodes]);

  useEffect(() => {
    if (!queryParams.email) {
      history.replace('/login/confirm-email');
      return;
    }
    history.replace('/invite');
    setEmailParam(queryParams.email);
    if (queryParams.token) {
      setTokenParam(queryParams.token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const linkAndLoginUser = async (token: string) => {
    if (!token) return;
    if (!emailParam) return;

    try {
      const linkResult = await linkRappiUser({
        email: emailParam,
        token,
      });
      if (linkResult.errors.some((error) => error.includes('user is already linked'))) {
        toast.add({
          type: 'success',
          message: t('invite.redirectToLinkedAccount'),
        });
      }
    } catch (e) {}

    history.push(`/?token=${token}`);
  };

  const handleWelcomeSubmit = async () => {
    setTermsAccepted(true);
    if (tokenParam) {
      await linkAndLoginUser(tokenParam);
      return;
    }

    history.push('/invite/rappi-account');
  };
  const handleRappiAccountSubmit = (hasAccount: boolean) => {
    setHasRappiAccount(hasAccount);
    if (hasAccount) history.push('/invite/telephone');
    else history.push('/invite/telephone/create');
  };
  const handleTelephoneSubmit = async (values: TelephoneValues, channel: OTPChannel, redirect = true) => {
    Sentry.addBreadcrumb({ category: 'invite', message: 'Telephone submit', data: { channel, values } });

    if (!channel) return;

    try {
      if (channel === 'whatsapp') {
        setOtpSendResponse(
          await otpService.sendWhatsAppOTP({
            country_code: values.country_code.code,
            phone: values.phone,
          }),
        );
      } else if (channel === 'sms') {
        setOtpSendResponse(
          await otpService.sendSMSOTP({
            country_code: values.country_code.code,
            phone: values.phone,
            locale: t('locale').substring(0, 2),
            via: 'sms',
          }),
        );
      }
      if (redirect) history.push('/invite/verification-code');
    } catch (e) {
      const errorCode = e.response?.data?.error?.code;

      if (errorCode) {
        toast.add({
          type: 'danger',
          message: t([`invite.error.${errorCode}` || e.response.data.error.message]),
        });
      } else {
        defaultErrorHandler(e);
      }
    } finally {
      setTelephone(values);
      setOtpChannel(channel);
    }
  };
  const handleResendCode = () => {
    if (!telephone || !otpChannel) return;
    return handleTelephoneSubmit(telephone, otpChannel, false);
  };
  const handleVerificationCodeSubmit = async (values: VerificationCodeValues) => {
    Sentry.addBreadcrumb({ category: 'invite', message: 'Verification code submit', data: { otpChannel, values } });

    if (!telephone || !otpSendResponse || !otpChannel || !emailParam) return;

    const phone = telephone.phone;
    const country_code = telephone.country_code.code;

    try {
      let otpValidateResponse;
      if (otpChannel === 'whatsapp') {
        otpValidateResponse = await otpService.validateWhatsAppOTP({
          country_code,
          phone,
          code: values.code,
        });
      } else if (otpChannel === 'sms') {
        otpValidateResponse = await otpService.validateSMSOTP({
          country_code,
          phone,
          code: values.code,
          uuid: (otpSendResponse as otpService.CreateSMSOTPResponse).sid,
        });
      }

      if (!otpValidateResponse.success && !otpValidateResponse.valid) {
        Sentry.addBreadcrumb({ category: 'invite', message: 'OTP validate response fail', data: otpValidateResponse });
        Sentry.captureMessage('[Invite] OTP validate response fail');
        toast.add({ type: 'danger', message: t('invite.error.invalid_code') });
        return;
      }

      let otpAuthenticateResponse;
      if (otpChannel === 'whatsapp') {
        otpAuthenticateResponse = await otpService.authenticateWhatsAppOTP({
          accessToken: (otpSendResponse as otpService.CreateWhatsAppOTPResponse).uuid,
          email: '',
          first_name: '',
          last_name: '',
          name: '',
          gender: '',
          replacement_criteria_id: 'replace',
          scope: 'all',
          social_id: phone,
          global_phone_number: `${country_code}${phone}`,
          sms_code: '',
          validate_code: false,
          phone,
          country_code,
        });
      } else if (otpChannel === 'sms') {
        otpAuthenticateResponse = await otpService.authenticateSMSOTP({
          accessToken: (otpSendResponse as otpService.CreateSMSOTPResponse).sid,
          country_code,
          phone,
          scope: 'all',
          social_id: phone,
        });
      }

      Sentry.addBreadcrumb({ category: 'invite', message: 'OTP authenticate response', data: otpAuthenticateResponse });

      Sentry.addBreadcrumb({ category: 'invite', message: 'OTP authenticate response', data: otpAuthenticateResponse });

      Sentry.addBreadcrumb({ category: 'invite', message: 'OTP authenticate response', data: otpAuthenticateResponse });

      await linkAndLoginUser(otpAuthenticateResponse.access_token);
    } catch (e) {
      const status = e.response?.status;
      const errors = e.response?.data?.errors;
      const error = e.response?.data?.error;

      if (status === 400) {
        if (errors?.name || errors?.nombre || errors?.email) {
          Sentry.addBreadcrumb({ category: 'invite', message: "OTP responded that the Rappi user doesn't exist" });
          history.push('/invite/join-rappi');
        } else if (error.code === 'verification_code_required') {
          Sentry.addBreadcrumb({ category: 'invite', message: '2FA required for this user', data: error });
          setTwoFactorData(error);
          history.push('/invite/two-factor');
        } else {
          defaultErrorHandler(e);
        }
      } else {
        defaultErrorHandler(e);
      }

      Sentry.withScope(function (scope) {
        scope.setTag('email', emailParam ?? '');
        scope.setTag('country_code', country_code ?? '');
        scope.setTag('phone', phone ?? '');
        scope.setTag('channel', otpChannel ?? '');

        Sentry.captureMessage('[Invite] Error on verification code submit');
      });
    } finally {
      setVerificationCode(values);
    }
  };
  const handleResendTwoFactorCode = async () => {
    if (!twoFactorData) return;
    try {
      await resendTwoFactor(twoFactorData.key);
    } catch (e) {
      const errorCode = e.response?.data?.error?.code;

      Sentry.addBreadcrumb({
        category: 'invite',
        message: '2FA code resend failed',
        data: { code: errorCode, response: e.response },
      });

      if (errorCode) {
        toast.add({ type: 'danger', message: t([`invite.error.${errorCode}` || e.response.data.error.message]) });
        history.replace('/invite/telephone');
      } else {
        defaultErrorHandler(e);
      }

      Sentry.captureMessage('[Invite] Failed to resend 2FA code');
    }
  };
  const handleTwoFactorCodeSubmit = async (values: TwoFactorCodeValues) => {
    Sentry.addBreadcrumb({ category: 'invite', message: 'Two factor code submit', data: values });

    if (!telephone || !twoFactorData || !otpSendResponse || !otpChannel) return;

    const phone = telephone.phone;
    const country_code = telephone.country_code.code;

    try {
      const otpAuth = {
        'x-two-factor-auth': twoFactorData.key,
        'x-two-factor-code': values.code,
      };

      let otpAuthenticateResponse;
      if (otpChannel === 'whatsapp') {
        otpAuthenticateResponse = await otpService.authenticateWhatsAppOTP(
          {
            accessToken: (otpSendResponse as otpService.CreateWhatsAppOTPResponse).uuid,
            email: '',
            first_name: '',
            last_name: '',
            name: '',
            gender: '',
            replacement_criteria_id: 'replace',
            scope: 'all',
            social_id: phone,
            global_phone_number: `${country_code}${phone}`,
            sms_code: '',
            validate_code: false,
            phone,
            country_code,
          },
          otpAuth,
        );
      } else if (otpChannel === 'sms') {
        otpAuthenticateResponse = await otpService.authenticateSMSOTP(
          {
            accessToken: (otpSendResponse as otpService.CreateSMSOTPResponse).sid,
            country_code,
            phone,
            scope: 'all',
            social_id: phone,
          },
          otpAuth,
        );
      }

      Sentry.addBreadcrumb({
        category: 'invite',
        message: 'Two factor OTP authenticate response',
        data: otpAuthenticateResponse,
      });

      await linkAndLoginUser(otpAuthenticateResponse.access_token);
    } catch (e) {
      const error = e.response?.data?.error;
      const status = e.response?.status;

      if (status === 400 && error?.code === 'invalid_code') {
        Sentry.captureMessage('[Invite] 2FA code is invalid');
        toast.add({ type: 'danger', message: t('invite.error.invalid_code') });
        return;
      }

      defaultErrorHandler(e);
      Sentry.captureMessage('[Invite] Error on 2FA verification code submit');
    } finally {
      setTwoFactorCode(values);
    }
  };

  const countryCodes: CountryCode[] = useMemo(
    () =>
      restCountries
        .filter((c) => c.callingCodes.length > 0 && c.callingCodes[0])
        .map((c) => ({
          id: c.alpha2Code,
          name: c.name,
          flag: c.flag,
          code: `+${c.callingCodes[0]}`,
        }))
        .sort((a, b) => (Number(a.code.replace(/[\D]/g, '')) > Number(b.code.replace(/[\D]/g, '')) ? 1 : -1)),
    [restCountries],
  );

  const current = history.location.pathname.split('/').last();
  const getAppBarTitle = () => {
    if (current === 'invite') return '';
    return t('invite.mobileTitle');
  };
  const getSideAssetUrl = () => {
    if (current === 'invite') return '/images/registration-onboarding/plane.svg';
    if (current === 'rappi-account') return '/images/registration-onboarding/rocket.svg';
    if (current === 'join-rappi') return '/images/logo-rappi-oficial-white.svg';
    return undefined;
  };
  const getAllowBack = () => current !== 'invite';

  return (
    <RegistrationOnboardingLayout
      appBarTitle={getAppBarTitle()}
      current={current}
      sideAssetUrl={getSideAssetUrl()}
      noGoBack={!getAllowBack()}
    >
      <Switch
        render={({ children }: any) => (
          <Fader style={isMobile ? { height: '100%' } : {}} viewStyle={isMobile ? { height: '100%' } : {}}>
            {children}
          </Fader>
        )}
      >
        <Route
          exact
          path="/invite"
          render={() => (
            <Welcome onSubmit={handleWelcomeSubmit} accepted={termsAccepted} countryCode={telephone?.country_code} />
          )}
        />
        <Route
          exact
          path="/invite/rappi-account"
          render={() => <RappiAccount onSubmit={handleRappiAccountSubmit} inviteEmail={emailParam ?? ''} />}
        />
        <Route
          exact
          path="/invite/telephone"
          render={() => (
            <Telephone
              values={telephone}
              onSubmit={handleTelephoneSubmit}
              countryCodes={countryCodes}
              hasRappiAccount={hasRappiAccount}
            />
          )}
        />
        <Route
          exact
          path="/invite/telephone/create"
          render={() => (
            <Telephone
              variant="register-number"
              values={telephone}
              onSubmit={handleTelephoneSubmit}
              countryCodes={countryCodes}
              hasRappiAccount={hasRappiAccount}
            />
          )}
        />
        <Route
          exact
          path="/invite/telephone/incorrect-phone"
          render={() => (
            <Telephone
              variant="incorrect-number"
              values={telephone}
              onSubmit={handleTelephoneSubmit}
              countryCodes={countryCodes}
              hasRappiAccount={hasRappiAccount}
            />
          )}
        />
        <Route exact path="/invite/join-rappi" render={() => <JoinRappi telephone={telephone} />} />
        <Route
          exact
          path="/invite/verification-code"
          render={() => (
            <VerificationCode
              values={verificationCode}
              onSubmit={handleVerificationCodeSubmit}
              onResend={handleResendCode}
              telephone={telephone}
              channel={otpChannel}
            />
          )}
        />
        <Route
          exact
          path="/invite/two-factor"
          component={() => (
            <TwoFactor
              values={twoFactorCode}
              onSubmit={handleTwoFactorCodeSubmit}
              onResend={handleResendTwoFactorCode}
              data={twoFactorData}
              telephone={telephone}
            />
          )}
        />
      </Switch>
    </RegistrationOnboardingLayout>
  );
};

export default Invite;
